import { supplierSchema, type SupplierInput } from "@erp/shared";
import { useForm } from "@tanstack/react-form";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { AddressFields } from "~/components/address-fields";
import { Button } from "~/components/ui/button";
import { Field, FieldGroup } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import { Select } from "~/components/ui/select";
import { Sheet } from "~/components/ui/sheet";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { addressDraft, addressOrNull, errorAt, schemaErrors, type AddressDraft, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";

export const supplierQuery = (id: string) =>
  queryOptions({ queryKey: ["suppliers", id], queryFn: () => unwrap(api.suppliers({ id }).get()) });

export type Supplier = Awaited<ReturnType<NonNullable<ReturnType<typeof supplierQuery>["queryFn"]>>>;

/** Currencies Phygiatech actually buys in; the API accepts any ISO 4217 code. */
const CURRENCIES = ["EUR", "USD", "CNY", "GBP", "CHF"] as const;

interface Draft {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  address: AddressDraft;
  currency: string;
  language: "fr" | "en";
  leadTimeDays: string;
  vatNumber: string;
  notes: string;
}

function toDraft(s?: Supplier): Draft {
  return {
    name: s?.name ?? "",
    contactName: s?.contactName ?? "",
    email: s?.email ?? "",
    phone: s?.phone ?? "",
    website: s?.website ?? "",
    address: addressDraft(s?.address ?? null),
    currency: s?.currency ?? "EUR",
    language: s?.language ?? "fr",
    leadTimeDays: s?.leadTimeDays != null ? String(s.leadTimeDays) : "",
    vatNumber: s?.vatNumber ?? "",
    notes: s?.notes ?? "",
  };
}

function toPayload(d: Draft): SupplierInput {
  return { ...d, address: addressOrNull(d.address), leadTimeDays: d.leadTimeDays.trim() === "" ? null : d.leadTimeDays };
}

const FORM_ID = "supplier-form";

export function SupplierFormSheet({
  open,
  onOpenChange,
  supplier,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Supplier;
  onSaved?: (s: Supplier) => void;
}) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={supplier ? supplier.name : t("suppliers.new")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID} loading={saving}>
            {supplier ? t("common.actions.save") : t("common.actions.create")}
          </Button>
        </>
      }
    >
      {open ? (
        <SupplierForm
          supplier={supplier}
          onSavingChange={setSaving}
          onDone={(saved) => {
            onOpenChange(false);
            onSaved?.(saved);
          }}
        />
      ) : null}
    </Sheet>
  );
}

function SupplierForm({
  supplier,
  onDone,
  onSavingChange,
}: {
  supplier?: Supplier;
  onDone: (s: Supplier) => void;
  onSavingChange: (saving: boolean) => void;
}) {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});

  const save = useMutation({
    mutationFn: (draft: Draft) => {
      const data = supplierSchema.parse(toPayload(draft));
      return supplier ? unwrap(api.suppliers({ id: supplier.id }).put(data)) : unwrap(api.suppliers.post(data));
    },
    onMutate: () => onSavingChange(true),
    onSettled: () => onSavingChange(false),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success(supplier ? t("suppliers.updated") : t("suppliers.created"));
      onDone(saved);
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const form = useForm({
    defaultValues: toDraft(supplier),
    onSubmit: async ({ value }) => {
      setSubmitted(true);
      if (Object.keys(schemaErrors(supplierSchema, toPayload(value))).length > 0) return;
      await save.mutateAsync(value).catch(() => undefined);
    },
  });

  const currencies = CURRENCIES.map((code) => ({ value: code as string, label: `${code} — ${format.currency(code)}` }));

  return (
    <form
      id={FORM_ID}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.Subscribe selector={(s) => s.values}>
        {(values) => {
          const errors: ErrorMap = { ...serverErrors, ...(submitted ? schemaErrors(supplierSchema, toPayload(values)) : {}) };
          const err = (path: string) => errorAt(t, errors, path);
          const text = (
            name: "name" | "contactName" | "email" | "phone" | "website" | "vatNumber",
            label: string,
            optional = true,
            type = "text",
          ) => (
            <form.Field name={name}>
              {(field) => (
                <Field label={label} error={err(name)} optionalLabel={optional ? t("common.states.optional") : undefined}>
                  <Input type={type} value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />
                </Field>
              )}
            </form.Field>
          );
          return (
            <div className="flex flex-col gap-7">
              <FieldGroup>
                {text("name", t("common.fields.name"), false)}
                <div className="grid gap-4 sm:grid-cols-2">
                  {text("contactName", t("suppliers.fields.contactName"))}
                  {text("email", t("common.fields.email"), true, "email")}
                  {text("phone", t("common.fields.phone"), true, "tel")}
                  {text("website", t("common.fields.website"), true, "url")}
                </div>
              </FieldGroup>
              <FieldGroup>
                <div className="grid gap-4 sm:grid-cols-2">
                  <form.Field name="currency">
                    {(field) => (
                      <Field label={t("suppliers.fields.currency")}>
                        <Select value={field.state.value} onValueChange={field.handleChange} options={currencies} />
                      </Field>
                    )}
                  </form.Field>
                  <form.Field name="language">
                    {(field) => (
                      <Field label={t("suppliers.fields.language")} hint={t("suppliers.languageHint")}>
                        <Select
                          value={field.state.value}
                          onValueChange={field.handleChange}
                          options={(["fr", "en"] as const).map((code) => ({
                            value: code,
                            label: t(`suppliers.languages.${code}`),
                          }))}
                        />
                      </Field>
                    )}
                  </form.Field>
                  <form.Field name="leadTimeDays">
                    {(field) => (
                      <Field
                        label={t("suppliers.fields.leadTimeDays")}
                        error={err("leadTimeDays")}
                        optionalLabel={t("common.states.optional")}
                      >
                        <Input
                          inputMode="numeric"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          className="tabular"
                        />
                      </Field>
                    )}
                  </form.Field>
                  {text("vatNumber", t("customers.fields.vatNumber"))}
                </div>
              </FieldGroup>
              <FieldGroup title={t("common.fields.address")}>
                <form.Field name="address">
                  {(field) => (
                    <AddressFields value={field.state.value} onChange={field.handleChange} errors={errors} prefix="address" />
                  )}
                </form.Field>
              </FieldGroup>
              <form.Field name="notes">
                {(field) => (
                  <Field label={t("common.fields.notes")} optionalLabel={t("common.states.optional")}>
                    <Textarea value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} rows={4} />
                  </Field>
                )}
              </form.Field>
            </div>
          );
        }}
      </form.Subscribe>
    </form>
  );
}
